// Persist + restore the operator's last map viewport per user. Stored in
// localStorage keyed by userId, so two operators sharing a browser do not
// clobber each other's view and signing in on a different device just gets a
// fresh default until that device builds its own history.
//
// Stays local on purpose: the value updates on every map idle (pans / zooms /
// searches), which would be a chatty round-trip if we synced to the DB. The
// data is also not sensitive and trivially regenerable.

const VERSION = 1;
const PREFIX = 'fttx.operator.mapView.';

export function keyFor(userId) {
  return PREFIX + (userId || 'anon');
}

// Returns { center: {lat,lng}, zoom } when a valid view is stored for this
// user, otherwise null. Never throws; localStorage may be disabled in
// privacy modes / blocked by the browser.
export function loadMapView(userId) {
  if (!userId) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || v.v !== VERSION) return null;
    const c = v.center;
    if (!c || !Number.isFinite(c.lat) || !Number.isFinite(c.lng)) return null;
    if (c.lat < -90 || c.lat > 90 || c.lng < -180 || c.lng > 180) return null;
    return {
      center: { lat: c.lat, lng: c.lng },
      zoom: Number.isFinite(v.zoom) ? v.zoom : null,
      savedAt: typeof v.savedAt === 'string' ? v.savedAt : null,
    };
  } catch (e) {
    return null;
  }
}

export function saveMapView(userId, view) {
  if (!userId || !view || !view.center) return;
  const { lat, lng } = view.center;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  try {
    const payload = {
      v: VERSION,
      center: { lat, lng },
      zoom: Number.isFinite(view.zoom) ? view.zoom : null,
      savedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(keyFor(userId), JSON.stringify(payload));
  } catch (e) {
    // localStorage might be full or blocked; the user just loses persistence,
    // which is a graceful degradation - the map still works.
  }
}

export function clearMapView(userId) {
  if (!userId) return;
  try { window.localStorage.removeItem(keyFor(userId)); } catch (e) { /* */ }
}
